import React, { useState, useEffect, useRef, useCallback } from 'react';
import '../styles/RicercaComune.css';
import intestazioniTablet from '../utils/chiaveDispositivo';
import { luogoDalCF } from '../utils/codiceFiscale';
import { chiediLuogo } from '../utils/luogoNascita';

/**
 * RicercaStato — lo Stato di nascita, scelto dall'elenco invece che scritto
 * (3 ottobre 2026, ramo cf-estero).
 *
 * Scritto a mano lo stesso Stato arrivava in tre grafie, come i comuni prima
 * di RicercaComune. L'elenco e' quello degli Stati esteri coi codici del
 * codice fiscale (stati_esteri, migration 287, Istat), anche quelli cessati:
 * chi e' nato in Jugoslavia nel 1960 lo trova. Si trova anche per il nome di
 * tutti i giorni (USA, Inghilterra, Olanda). Il campo resta testo: un valore
 * che l'elenco non ha si puo' scrivere lo stesso, come per il comune.
 *
 * Passando il codice fiscale, segnala se lo Stato scritto non e' quello
 * della Z del codice, e se il codice e' di un comune ceduto nel 1947, per
 * cui lo Stato non si scrive (segnala, non rifiuta: decide chi compila). Al
 * server va il codice del luogo, mai il codice fiscale.
 *
 * Funziona sia dentro il gestionale sia sul tablet: se riceve la chiave del
 * dispositivo la usa al posto del gettone, nell'intestazione.
 *
 * Usa le classi di RicercaComune (rcom-): e' lo stesso campo, per un'altra
 * tabella.
 *
 * Props:
 *  - valore, onChange(nome)   il nome dello Stato
 *  - codiceFiscale            per la coerenza con la Z
 *  - chiave, grande, errore, placeholder, id
 */
const RicercaStato = ({
    valore, onChange, chiave, grande, errore, placeholder, id, codiceFiscale,
}) => {
    const [risultati, setRisultati] = useState([]);
    const [aperto, setAperto] = useState(false);
    const [evidenziato, setEvidenziato] = useState(0);
    const [nota, setNota] = useState(null);
    const contenitore = useRef(null);
    const scelto = useRef(false);

    useEffect(() => {
        const fuori = (e) => {
            if (contenitore.current && !contenitore.current.contains(e.target)) setAperto(false);
        };
        document.addEventListener('mousedown', fuori);
        return () => document.removeEventListener('mousedown', fuori);
    }, []);

    const cerca = useCallback(async (q) => {
        if (!q || q.trim().length < 2) { setRisultati([]); return; }
        try {
            const par = new URLSearchParams({ q: q.trim(), limit: '8' });
            const intestazioni = intestazioniTablet(chiave);
            if (!chiave) {
                const token = localStorage.getItem('token');
                if (token) intestazioni.Authorization = `Bearer ${token}`;
            }
            const r = await fetch(`/api/comuni/stati?${par}`, { headers: intestazioni });
            const d = await r.json();
            setRisultati(d.success ? (d.data.stati || []) : []);
        } catch (e) {
            console.error('Ricerca Stato:', e);
            setRisultati([]);
        }
    }, [chiave]);

    useEffect(() => {
        if (scelto.current) { scelto.current = false; return; }
        const t = setTimeout(() => cerca(valore), 250);
        return () => clearTimeout(t);
    }, [valore, cerca]);

    /* Coerenza con il luogo del codice fiscale: per un codice valido, e solo
       con uno Stato scritto. La Z dice uno Stato, e si segnala se e' un
       altro; un comune ceduto nel 1947 (Pola, Fiume, Zara) si segnala
       sempre, perche' per chi e' nato li' lo Stato di oggi non si scrive
       (Alex, 3/10/2026: la legge 54 del 1989). Il motivo lo scrive il
       server; per un comune di oggi non ha niente da dire. */
    useEffect(() => {
        const l = luogoDalCF(codiceFiscale);
        if (!l || !String(valore || '').trim()) { setNota(null); return; }
        let attivo = true;
        const t = setTimeout(async () => {
            const d = await chiediLuogo(l.codice, { chiave, stato: valore });
            if (attivo) setNota(d && d.nota ? d.nota : null);
        }, 400);
        return () => { attivo = false; clearTimeout(t); };
    }, [codiceFiscale, valore, chiave]);

    const scegli = (s) => {
        scelto.current = true;
        onChange(s.nome);
        setAperto(false);
        setRisultati([]);
    };

    const tasti = (e) => {
        if (!aperto || !risultati.length) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setEvidenziato((i) => Math.min(i + 1, risultati.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setEvidenziato((i) => Math.max(i - 1, 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (risultati[evidenziato]) scegli(risultati[evidenziato]);
        } else if (e.key === 'Escape') {
            setAperto(false);
        }
    };

    return (
        <div className={`rcom ${grande ? 'rcom--grande' : ''}`} ref={contenitore}>
            <input
                id={id}
                className={errore ? 'rcom-input errore' : 'rcom-input'}
                value={valore || ''}
                placeholder={placeholder || 'Scrivi le prime lettere…'}
                /* La colonna e' varchar(60), come il nome piu' lungo
                   dell'elenco Istat: oltre, il server risponde 400. */
                maxLength={60}
                onChange={(e) => {
                    onChange(e.target.value);
                    setAperto(true);
                    setEvidenziato(0);
                }}
                onFocus={() => setAperto(true)}
                onKeyDown={tasti}
                autoComplete="off"
            />

            {nota && !aperto && <div className="rcom-incoerenza">{nota}</div>}

            {aperto && risultati.length > 0 && (
                <div className="rcom-lista">
                    {risultati.map((s, i) => (
                        <button
                            key={s.codice_catastale}
                            type="button"
                            className={`rcom-voce ${i === evidenziato ? 'evidenziata' : ''}`}
                            onMouseEnter={() => setEvidenziato(i)}
                            onClick={() => scegli(s)}
                        >
                            <span className="rcom-nome">{s.nome}</span>
                            <span className="rcom-prov">{s.cessato ? 'non esiste più' : ''}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default RicercaStato;
